/**
 * Content model for Primero Dental Clinic.
 *
 * This is the contract between content authors and page templates. It exists
 * because the previous content layer was a flat 308-key i18next namespace
 * (src/locales/*.json) validated only for top-level key parity — workable for
 * UI chrome, but unusable for 14 service pages plus doctor and case entities.
 *
 * Three properties matter more than convenience here:
 *
 * 1. **Both languages are required by the type system.** A `Localized<T>` field
 *    cannot be half-filled, so a service page can never ship with an Arabic
 *    route rendering English content — the failure mode the audit found on
 *    /zeraet-asnan-madinet-nasr.
 * 2. **Unverified and invented content is structurally distinguishable from
 *    fact.** Anything the clinic has not confirmed is either absent or carries
 *    an explicit `provenance`, so a template can refuse to render a credential
 *    it cannot stand behind. Nothing about a DEMO record should be able to
 *    reach a page looking like a verified one.
 * 3. **Medical claims carry their basis.** A statement about a treatment either
 *    is general patient education, or is a clinic-specific claim requiring
 *    evidence. The type forces the author to say which.
 */

export type Locale = "en" | "ar";

export const LOCALES: readonly Locale[] = ["en", "ar"] as const;

/** A value that must exist in both languages. */
export type Localized<T> = { readonly [L in Locale]: T };

/**
 * Where a piece of content came from, and therefore how far it may be trusted.
 *
 * - `editorial`   — written by us from research; general patient education. Safe.
 * - `client`      — supplied and confirmed by the clinic. Safe.
 * - `unverified`  — asserted by the previous site but never confirmed. Renders
 *                   only where a human has accepted the risk; registered in
 *                   src/data/clinic.ts CLINIC_VERIFICATION.
 * - `demo`        — invented for development. MUST be visibly labelled wherever
 *                   it renders, and MUST NOT be emitted into structured data.
 */
export type Provenance = "editorial" | "client" | "unverified" | "demo";

/** Content whose trustworthiness must travel with it. */
export interface Attributed<T> {
  readonly value: T;
  readonly provenance: Provenance;
  /** For `demo`/`unverified`: what the client must supply to replace this. */
  readonly replaceWith?: string;
}

// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------

export interface Faq {
  readonly question: Localized<string>;
  readonly answer: Localized<string>;
  /**
   * True when the answer touches clinical substance and should be reviewed by a
   * dentist before publication. Surfaced in docs/content-roadmap.md.
   */
  readonly needsClinicalReview: boolean;
}

export interface ProcessStep {
  readonly title: Localized<string>;
  readonly body: Localized<string>;
}

/** A row in a treatment-comparison table (e.g. implants vs bridge vs denture). */
export interface ComparisonRow {
  readonly option: Localized<string>;
  readonly bestFor: Localized<string>;
  readonly considerations: Localized<string>;
}

export interface ImageRef {
  /** Path under /public. */
  readonly src: string;
  /** Meaningful, non-keyword-stuffed, per language. */
  readonly alt: Localized<string>;
  readonly width: number;
  readonly height: number;
  readonly provenance: Provenance;
}

// ---------------------------------------------------------------------------
// Services
// ---------------------------------------------------------------------------

/**
 * Slugs are identical across locales so hreflang pairing is mechanical:
 * /en/services/<slug> <-> /ar/services/<slug>.
 */
export type ServiceSlug =
  | "dental-implants"
  | "cosmetic-dentistry"
  | "veneers"
  | "hollywood-smile"
  | "orthodontics"
  | "root-canal-treatment"
  | "pediatric-dentistry"
  /**
   * Not in the original seven, but /oral-surgery is an existing indexed URL
   * with a prerendered page. Dropping it to match a list exactly would discard
   * whatever equity it has for no benefit, so it is carried forward as a
   * first-class service rather than redirected into an unrelated page.
   */
  | "oral-surgery";

export const SERVICE_SLUGS: readonly ServiceSlug[] = [
  "dental-implants",
  "cosmetic-dentistry",
  "veneers",
  "hollywood-smile",
  "orthodontics",
  "root-canal-treatment",
  "pediatric-dentistry",
  "oral-surgery",
] as const;

/**
 * The canonical service-page outline, derived from the search-intent research.
 *
 * Every section is required except those explicitly optional, so no service
 * page can quietly become a thin keyword page — the specific failure the brief
 * forbids. Sections that would require clinic-specific evidence
 * (`technology`, `materials`) are optional precisely so they can be omitted
 * rather than invented.
 */
export interface ServiceContent {
  readonly slug: ServiceSlug;

  /** Navigation/card label. Short. */
  readonly name: Localized<string>;

  /** SEO metadata, authored per locale — never translated word-for-word. */
  readonly meta: Localized<{
    readonly title: string;
    readonly description: string;
    /** Social headline, when the SERP title reads poorly as a share card. */
    readonly ogTitle?: string;
    readonly ogDescription?: string;
  }>;

  /** The single page H1. */
  readonly h1: Localized<string>;

  /** One-paragraph plain-language answer to "what is this". */
  readonly summary: Localized<string>;

  /** Who the treatment suits, as bullet points. */
  readonly candidacy: Localized<readonly string[]>;

  /** Problems patients come in with that this addresses. */
  readonly problemsAddressed: Localized<readonly string[]>;

  /** Treatment types/options within this service. */
  readonly options: readonly {
    readonly name: Localized<string>;
    readonly body: Localized<string>;
  }[];

  /** How diagnosis and planning actually work. */
  readonly diagnosis: Localized<string>;

  /** Step-by-step treatment journey. */
  readonly process: readonly ProcessStep[];

  /**
   * Technology used. Optional and attributed: naming equipment the clinic may
   * not own is exactly the fabrication the brief prohibits.
   */
  readonly technology?: Attributed<Localized<readonly string[]>>;

  /** Materials/brands. Same reasoning as `technology`. */
  readonly materials?: Attributed<Localized<readonly string[]>>;

  /** Recovery and aftercare, where medically applicable. */
  readonly aftercare?: Localized<readonly string[]>;

  /**
   * Risks, limitations and contraindications. Required — a healthcare page that
   * only lists benefits is not trustworthy, and this is the section competitors
   * consistently omit.
   */
  readonly risksAndLimits: Localized<readonly string[]>;

  /** Realistic timeframe framing. Must not promise a specific duration. */
  readonly timeline: Localized<string>;

  /** Alternatives comparison. */
  readonly comparison?: {
    readonly heading: Localized<string>;
    readonly rows: readonly ComparisonRow[];
  };

  /**
   * What drives cost — never fixed prices, which are unverified and volatile.
   */
  readonly costFactors: Localized<readonly string[]>;

  readonly faqs: readonly Faq[];

  /** Internal linking: sibling services this page should link to. */
  readonly relatedServices: readonly ServiceSlug[];

  /** Doctor slugs who perform this treatment. */
  readonly relatedDoctors: readonly string[];

  /** Case slugs demonstrating this treatment. */
  readonly relatedCases: readonly string[];

  /**
   * schema.org MedicalProcedure name, when the service maps to a real
   * procedure. Omitted for marketing-package pages such as hollywood-smile,
   * which is not a distinct clinical procedure and must not be marked up as one.
   */
  readonly procedureType?: string;
}

// ---------------------------------------------------------------------------
// Doctors
// ---------------------------------------------------------------------------

/**
 * A doctor profile.
 *
 * The clinic has supplied no verified roster, so every record shipped today is
 * `provenance: "demo"`. Credential fields are `Attributed` individually rather
 * than at record level, so a real name can be filled in ahead of a real licence
 * number without the whole profile silently becoming "verified".
 *
 * Templates MUST NOT emit Person/Physician structured data for a record whose
 * `provenance` is `demo` — marking up invented credentials as machine-readable
 * fact is the worst available outcome here.
 */
export interface Doctor {
  readonly slug: string;
  readonly provenance: Provenance;

  readonly name: Localized<string>;
  readonly specialty: Localized<string>;

  readonly photo: ImageRef;

  /** Two-to-three paragraph biography. */
  readonly bio: Localized<readonly string[]>;

  readonly education: Attributed<Localized<readonly string[]>>;
  readonly certifications: Attributed<Localized<readonly string[]>>;
  readonly memberships: Attributed<Localized<readonly string[]>>;
  readonly yearsExperience: Attributed<number>;

  /** Languages spoken, as ISO-ish display strings per locale. */
  readonly languages: Localized<readonly string[]>;

  readonly clinicalInterests: Localized<readonly string[]>;

  /** Services this doctor provides. Drives doctor <-> service linking. */
  readonly services: readonly ServiceSlug[];

  readonly cases: readonly string[];
}

// ---------------------------------------------------------------------------
// Clinical cases
// ---------------------------------------------------------------------------

/**
 * A before/after clinical case.
 *
 * `consent` is required and has no default. A case without recorded consent
 * must not render patient imagery, regardless of how good the photos are —
 * so the type makes the absence of consent impossible to overlook.
 */
export interface ClinicalCase {
  readonly slug: string;
  readonly provenance: Provenance;

  readonly title: Localized<string>;
  readonly service: ServiceSlug;

  /** The patient's presenting concern, in their terms. */
  readonly concern: Localized<string>;
  readonly startingCondition: Localized<string>;
  readonly diagnosis: Localized<string>;
  readonly treatmentPlan: Localized<string>;
  readonly steps: readonly ProcessStep[];
  readonly technologyUsed?: Attributed<Localized<readonly string[]>>;
  readonly result: Localized<string>;

  /** Only stated when verified; never estimated to look impressive. */
  readonly timeframe?: Attributed<Localized<string>>;

  readonly doctor?: string;

  readonly images: {
    readonly before?: ImageRef;
    readonly after?: ImageRef;
  };

  /**
   * Consent status for publishing this patient's imagery and details.
   * `"unknown"` blocks image rendering.
   */
  readonly consent: "documented" | "unknown";
}

// ---------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------

/** True when content is safe to present as verified fact. */
export function isTrustworthy(p: Provenance): boolean {
  return p === "editorial" || p === "client";
}

/**
 * True when a record may be emitted into structured data.
 *
 * Deliberately stricter than `isTrustworthy`: editorial prose is fine to show a
 * human, but only client-confirmed facts belong in machine-readable markup that
 * search engines may surface as attributed claims.
 */
export function isSchemaEligible(p: Provenance): boolean {
  return p === "client";
}

/** True when a case's imagery may be published. */
export function mayPublishCaseImages(c: ClinicalCase): boolean {
  return c.consent === "documented";
}
